/**
 * Token custody, deliberately kept dependency-free so both the HTTP client and
 * the auth store can use it without an import cycle.
 *
 * Trade-off: the access token lives in memory only (it dies with the tab, and
 * XSS cannot read it from storage), while the refresh token is persisted so a
 * reload does not sign the user out. The backend hands both back in the login
 * body — moving the refresh token to an httpOnly cookie is a backend change and
 * is tracked in the project plan.
 */
const REFRESH_KEY = 'uruanna.refreshToken';

type Listener = () => void;

let accessToken: string | null = null;
let expiresAt = 0;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

export const session = {
  get accessToken() {
    return accessToken;
  },
  get refreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  /** True when an access token exists and is not within its refresh window. */
  isFresh(skewMs = 15_000): boolean {
    return Boolean(accessToken) && Date.now() < expiresAt - skewMs;
  },
  set(tokens: { accessToken?: string; refreshToken?: string; expiresIn?: number }) {
    if (tokens.accessToken) accessToken = tokens.accessToken;
    if (tokens.expiresIn) expiresAt = Date.now() + tokens.expiresIn * 1000;
    if (tokens.refreshToken) {
      try {
        localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
      } catch {
        /* private mode — the session simply will not survive a reload */
      }
    }
    emit();
  },
  clear() {
    accessToken = null;
    expiresAt = 0;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
    emit();
  },
  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
